/**
 * フォームバリデーション
 */

export default class FormValidation {
  constructor() {
    this.form = document.querySelector('#js-form');
    if (!this.form) return;

    this.submitButton = this.form.querySelector('button[type="submit"]');
    this.validatedFields = new Set(); // 一度エラーになった要素を記録
    this.errorClass = 'is-error';
    this.showErrorClass = 'is-show';
  }

  /**
   * エラーメッセージを表示
   * @param {string} errorKey - エラーキー
   * @param {boolean} isFormatError - 形式エラーかどうか
   */
  showError(errorKey, isFormatError = false) {
    // まず既存のエラーメッセージを非表示にする
    this.hideError(errorKey);

    // 形式エラーの場合は形式エラーメッセージを表示
    if (isFormatError) {
      const formatErrorMessage = this.form.querySelector(`[data-error-message-format="${errorKey}"]`);
      if (formatErrorMessage) {
        formatErrorMessage.classList.add(this.showErrorClass);
        return;
      }
    }
    
    // 必須エラーの場合は必須エラーメッセージを表示
    const errorMessage = this.form.querySelector(`[data-error-message="${errorKey}"]`);
    if (errorMessage) {
      errorMessage.classList.add(this.showErrorClass);
    }
  }

  /**
   * エラーメッセージを非表示
   */
  hideError(errorKey) {
    const errorMessage = this.form.querySelector(`[data-error-message="${errorKey}"]`);
    if (errorMessage) {
      errorMessage.classList.remove(this.showErrorClass);
    }
    const formatErrorMessage = this.form.querySelector(`[data-error-message-format="${errorKey}"]`);
    if (formatErrorMessage) {
      formatErrorMessage.classList.remove(this.showErrorClass);
    }
  }

  /**
   * 要素にエラークラスを追加
   */
  addErrorClass(elements) {
    if (!Array.isArray(elements)) {
      elements = [elements];
    }
    elements.forEach(element => {
      if (element) {
        element.classList.add(this.errorClass);
      }
    });
  }

  /**
   * 要素からエラークラスを削除
   */
  removeErrorClass(elements) {
    if (!Array.isArray(elements)) {
      elements = [elements];
    }
    elements.forEach(element => {
      if (element) {
        element.classList.remove(this.errorClass);
      }
    });
  }

  /**
   * メールアドレスの形式チェック
   */
  validateEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * 電話番号の形式チェック（ハイフンありなしどちらでもOK）
   */
  validateTel(tel) {
    // ハイフンを除去して数字のみかチェック
    const telWithoutHyphen = tel.replace(/-/g, '');
    // 10桁または11桁の数字
    const telRegex = /^\d{10,11}$/;
    return telRegex.test(telWithoutHyphen);
  }

  /**
   * 単一の必須フィールドをバリデーション
   */
  validateRequiredField(errorKey) {
    const field = this.form.querySelector(`[data-required="${errorKey}"]`);
    if (!field) return true;

    const fieldType = field.type;
    let isValid = false;
    let elementsToValidate = [field];
    const value = field.value.trim();

    if (fieldType === 'radio') {
      // ラジオボタン: 同じnameのいずれかがチェックされていればOK
      const radioGroup = this.form.querySelectorAll(`input[type="radio"][name="${field.name}"]`);
      isValid = Array.from(radioGroup).some(radio => radio.checked);
      elementsToValidate = Array.from(radioGroup);
    } else if (fieldType === 'checkbox') {
      // チェックボックス: 同じnameのいずれかがチェックされていればOK（単体でもOK）
      const checkboxGroup = this.form.querySelectorAll(`input[type="checkbox"][name="${field.name}"]`);
      isValid = Array.from(checkboxGroup).some(checkbox => checkbox.checked);
      elementsToValidate = Array.from(checkboxGroup);
    } else if (fieldType === 'email') {
      // メールアドレス: 入力されているか、かつ形式が正しいか
      if (value === '') {
        isValid = false;
      } else if (!this.validateEmail(value)) {
        // 形式エラー
        isValid = false;
        this.showError(errorKey, true);
        this.addErrorClass(elementsToValidate);
        this.validatedFields.add(errorKey);
        return false;
      } else {
        isValid = true;
      }
    } else if (fieldType === 'tel') {
      // 電話番号: 入力されているか、かつ形式が正しいか
      if (value === '') {
        isValid = false;
      } else if (!this.validateTel(value)) {
        // 形式エラー
        isValid = false;
        this.showError(errorKey, true);
        this.addErrorClass(elementsToValidate);
        this.validatedFields.add(errorKey);
        return false;
      } else {
        isValid = true;
      }
    } else {
      // テキスト入力など: 値が入力されているか
      isValid = value !== '';
    }

    if (!isValid) {
      this.showError(errorKey, false);
      this.addErrorClass(elementsToValidate);
      this.validatedFields.add(errorKey);
      return false;
    } else {
      this.hideError(errorKey);
      this.removeErrorClass(elementsToValidate);
      return true;
    }
  }

  /**
   * セットの必須フィールドをバリデーション（data-required-set）
   * 例：名前の姓と名
   */
  validateRequiredSet(errorKey) {
    const fields = this.form.querySelectorAll(`[data-required-set="${errorKey}"]`);
    if (fields.length === 0) return true;

    // すべてのフィールドが入力されているかチェック
    const allFilled = Array.from(fields).every(field => field.value.trim() !== '');

    if (!allFilled) {
      this.showError(errorKey);
      this.addErrorClass(Array.from(fields));
      this.validatedFields.add(errorKey);
      return false;
    } else {
      this.hideError(errorKey);
      this.removeErrorClass(Array.from(fields));
      return true;
    }
  }

  /**
   * すべてのフィールドをバリデーション
   */
  validateAll() {
    let isValid = true;

    // data-required のフィールドをバリデーション
    const requiredFields = this.form.querySelectorAll('[data-required]');
    const requiredKeys = new Set();
    requiredFields.forEach(field => {
      const errorKey = field.getAttribute('data-required');
      if (errorKey && !requiredKeys.has(errorKey)) {
        requiredKeys.add(errorKey);
        if (!this.validateRequiredField(errorKey)) {
          isValid = false;
        }
      }
    });

    // data-required-set のフィールドをバリデーション
    const requiredSetFields = this.form.querySelectorAll('[data-required-set]');
    const requiredSetKeys = new Set();
    requiredSetFields.forEach(field => {
      const errorKey = field.getAttribute('data-required-set');
      if (errorKey && !requiredSetKeys.has(errorKey)) {
        requiredSetKeys.add(errorKey);
        if (!this.validateRequiredSet(errorKey)) {
          isValid = false;
        }
      }
    });

    return isValid;
  }

  /**
   * 個別フィールドのリアルタイムバリデーション
   */
  validateFieldOnInput(field) {
    const errorKey = field.getAttribute('data-required') || field.getAttribute('data-required-set');
    if (!errorKey || !this.validatedFields.has(errorKey)) {
      return; // まだ送信されていない場合はバリデーションしない
    }

    // data-required-set の場合
    if (field.hasAttribute('data-required-set')) {
      this.validateRequiredSet(errorKey);
    } else {
      // data-required の場合
      this.validateRequiredField(errorKey);
    }
  }

  /**
   * イベントリスナーを追加
   */
  addEventListeners() {
    // フォーム送信時のバリデーション
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();

      if (!this.validateAll()) {
        // エラーがある場合は送信しない
        // 最初のエラーフィールドにスクロール
        const firstErrorField = this.form.querySelector(`.${this.errorClass}`);
        if (firstErrorField) {
          // ラジオボタンやチェックボックスはフォーカスできないので、スクロールのみ
          if (firstErrorField.type !== 'radio' && firstErrorField.type !== 'checkbox') {
            firstErrorField.focus();
          }
          // 固定ヘッダーの高さを考慮してスクロール（SmoothScrollと同じロジック）
          const fixHeader = document.querySelector('#js-header');
          const targetOffset = firstErrorField.getBoundingClientRect().top;
          const nowOffset = window.pageYOffset;
          const gap = fixHeader ? fixHeader.clientHeight : 0;
          const finishOffset = targetOffset + nowOffset - gap - 20; // 20pxの余白を追加
          window.scrollTo({
            top: finishOffset,
            behavior: 'smooth',
          });
        }
        return false;
      }

      // バリデーション成功時はサーバーに POST 送信
      this.form.submit();
    });

    // 入力中のリアルタイムバリデーション
    const allFields = this.form.querySelectorAll('input, textarea, select');
    allFields.forEach(field => {
      // inputイベント（テキスト入力）
      field.addEventListener('input', () => {
        this.validateFieldOnInput(field);
      });

      // changeイベント（ラジオボタン、チェックボックス、セレクトボックス）
      field.addEventListener('change', () => {
        this.validateFieldOnInput(field);
      });
    });
  }

  /**
   * 初期化
   */
  init() {
    if (!this.form) return;

    // エラーメッセージを初期状態で非表示にする
    const errorMessages = this.form.querySelectorAll('[data-error-message], [data-error-message-format]');
    errorMessages.forEach(errorMessage => {
      errorMessage.classList.remove(this.showErrorClass);
    });

    this.addEventListeners();
  }
}

